import React, { useEffect, useMemo, useRef, useState } from "react";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Stack,
  Skeleton,
  TextField,
  Button,
  Chip,
  IconButton,
  LinearProgress,
  InputAdornment,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import FilterAltOffIcon from "@mui/icons-material/FilterAltOff";
import VerifiedIcon from "@mui/icons-material/Verified";
import BlockIcon from "@mui/icons-material/Block";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import PublicIcon from "@mui/icons-material/Public";
import TopSection from "../../../LT/TopSection";
import ButtonComponent from "../../../../components/common/button/Button";
import { Loader } from "../../../../components/common/Loader";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import UploadDomainModal from "../UploadDomainModal";
import WhitelistDomainModal from "../WhitelistDomainModal";
import BlacklistDomainModal from "../BlacklistDomainModal";
import {
  getDomainDashboardSummaryEndpoint,
  getDomainListEndpoint,
  getEmailDomainListEndpoint,
  postAddDomainEndpoint,
  postUploadDomainEndpoint,
} from "../../../../common/endpoints";
import { getDomainListSlice } from "../../../../redux/actions/getDomainListSlice";
import { postUploadDomainSlice } from "../../../../redux/actions/postUploadDomainSlice";
import { getDomainDashboardSummarySlice } from "../../../../redux/actions/getDomainDashboardSummarySlice";
import AddDomainModal from "../AddDomainModal";
import { postAddDomainSlice } from "../../../../redux/actions/postAddDomainSlice";

const AdminManageWhitelistDomain = () => {
  const dispatch = useDispatch();
  const gridRef = useRef(null);
  const [gridApi, setGridApi] = useState();
  const [tableLoading, setTableLoading] = useState(false);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [selectedDomains, setSelectedDomains] = useState([]);
  const [disableFilterBtn, setDisableFilterBtn] = useState(true);
  const [openWhitelistModal, setOpenWhitelistModal] = useState(false);
  const [openBlacklistModal, setOpenBlacklistModal] = useState(false);
  const [openAddModal, setOpenAddModal] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [openAddDomainModal, setOpenAddDomainModal] = useState(false);
  const [allRowData, setAllRowData] = useState([]);
  const [dashboardFilter, setDashboardFilter] = useState("");
  const [summary, setSummary] = useState({
    totalDomains: 0,
    domainsWhitelisted: 0,
    domainsOnboarded: 0,
    pendingWhitelist: 0,
    pendingOnboard: 0,
  });
  const loggedInUser = localStorage.getItem("userEmail");

  const pagination = true;
  const paginationPageSize = 10;
  const paginationPageSizeSelector = [10, 25, 50, 100];

  const [rowData, setRowData] = useState([]);

  const onGridReady = (params) => {
    setGridApi(params.api);
    gridRef.current = params.api;
  };

  const onFilterChanged = (params) => {
    if (gridRef.current) {
      const filterModel = gridRef.current.getFilterModel();
      setDisableFilterBtn(Object.keys(filterModel).length === 0);
    }
  };

  const gridOptions = {
    getRowId: (params) => params.data.domain,
    onFilterChanged,
  };

  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      filter: "agMultiColumnFilter",
      resizable: true,
    }),
    [],
  );

  useEffect(() => {
    if (gridApi) {
      gridApi.setGridOption("quickFilterText", searchText);
    }
  }, [searchText, gridApi]);

  useEffect(() => {
    getDomainList();
    getDashboardSummary();
  }, []);

  const cards = [
    {
      title: "TOTAL PARENT VENDORS",
      value: summary.totalDomains,
      percent: 100,
      color: "#2563eb",
    },
    {
      title: "DOMAINS WHITELISTED",
      value: summary.domainsWhitelisted,
      percent: summary.totalDomains
        ? Math.round((summary.domainsWhitelisted / summary.totalDomains) * 100)
        : 0,
      color: "#16a34a",
    },
    {
      title: "PARENT VENDORS ONBOARDED",
      value: summary.domainsOnboarded,
      percent: summary.totalDomains
        ? Math.round((summary.domainsOnboarded / summary.totalDomains) * 100)
        : 0,
      color: "#f97316",
    },
    {
      title: "PENDING PARENT VENDOR WHITELIST",
      value: summary.pendingWhitelist,
      percent: summary.totalDomains
        ? Math.round((summary.pendingWhitelist / summary.totalDomains) * 100)
        : 0,
      color: "#7c3aed",
    },
    {
      title: "PENDING PARENT VENDOR ONBOARD",
      value: summary.pendingOnboard,
      percent: summary.totalDomains
        ? Math.round((summary.pendingOnboard / summary.totalDomains) * 100)
        : 0,
      color: "#dc2626",
    },
  ];

  const handleDashboardFilter = (type) => {
    setDashboardFilter(type);
    setDisableFilterBtn(false);

    switch (type) {
      case "WHITELISTED":
        setRowData(
          allRowData.filter(
            row => row.status === "ACTIVE" || row.status === "ONBOARDED"
          )
        );
        break;

      case "ONBOARDED":
        setRowData(
          allRowData.filter(row => row.status === "ONBOARDED")
        );
        break;

      case "PENDING":
        setRowData(
          allRowData.filter(
            row =>
              row.status !== "ACTIVE" &&
              row.status !== "ONBOARDED"
          )
        );
        break;

      default:
        setRowData(allRowData);
        setDashboardFilter("");
        setDisableFilterBtn(true);
    }
  };

  const getDashboardSummary = async () => {
    try {
      setSummaryLoading(true);
      const response = await dispatch(
        getDomainDashboardSummarySlice({
          dynamicEndpoint: getDomainDashboardSummaryEndpoint,
        }),
      );

      if (response?.meta?.requestStatus === "fulfilled") {
        setSummary(response.payload || {});
      } else {
        toast.error("Failed to fetch dashboard summary");
      }
    } catch (error) {
      console.error("Summary API error:", error);
      toast.error("Something went wrong");
    } finally {
      setSummaryLoading(false);
    }
  };

  const getDomainList = async () => {
    try {
      setTableLoading(true);

      const response = await dispatch(
        getDomainListSlice({
          dynamicEndpoint: getDomainListEndpoint,
        }),
      );

      if (response?.meta?.requestStatus === "fulfilled") {
        const list = response?.payload || [];

        const formattedData = list.map((item) => ({
          alIdentifier: item.id,
          requestNo: item.requestId,
          domain: item.domainName,
          requestedBy: item.requestedBy,
          email: item.requestedByEmail || "",
          requestDate: item.createdAt
            ? new Date(item.createdAt).toLocaleDateString("en-GB")
            : "",
          requestType:
            item.requestType === "ADD"
              ? "Whitelist"
              : item.requestType === "REMOVE"
                ? "Remove Whitelist"
                : item.requestType || "",
          status: item.status,
        }));

        setRowData(formattedData);
        setAllRowData(formattedData);
      } else {
        toast.error("Failed to fetch domain list.");
      }
    } catch (error) {
      console.error("Domain API error:", error);
      toast.error("Something went wrong.");
    } finally {
      setTableLoading(false);
    }
  };

  const canWhitelist =
    selectedDomains.length > 0 &&
    selectedDomains.every(
      (item) =>
        item.status === "UPLOADED" ||
        (item.status === "REQUEST_FAILED" && item.requestType === "Whitelist"))

  const canBlacklist =
    selectedDomains.length > 0 &&
    selectedDomains.every(
      (item) =>
        item.status === "ACTIVE" ||
        item.status === "ONBOARDED" ||
        (item.status === "REQUEST_FAILED" && item.requestType === "Remove Whitelist")
    );

  const rowSelection = {
    mode: "multiRow",
  };

  const columns = [
    {
      headerName: "AL Identifier",
      field: "alIdentifier",
      flex: 1.8,
    },
    {
      headerName: "Request No.",
      field: "requestNo",
      flex: 1.8,
    },
    {
      headerName: "Domain Name",
      field: "domain",
      flex: 2,
    },
    {
      field: "requestedBy",
      headerName: "Requested By",
      flex: 1.8,
      cellRenderer: ({ data }) => (
        <Box>
          <Typography fontSize={13}>{data.requestedBy}</Typography>

          <Typography fontSize={12} color="#98A2B3">
            {data.email}
          </Typography>
        </Box>
      ),
    },
    {
      field: "requestDate",
      headerName: "Request Date",
      flex: 1.8,
      cellRenderer: ({ data }) => (
        <Box>
          <Typography fontSize={13}>{data.requestDate}</Typography>
        </Box>
      ),
    },
    {
      headerName: "Request Type",
      field: "requestType",
      flex: 1.5,
      cellRenderer: ({ value }) => {
        if (!value) return null; // No chip for null/undefined/empty

        return (
          <Chip
            label={value}
            size="small"
            sx={{
              fontWeight: 600,
              borderRadius: "8px",
              backgroundColor:
                value === "Whitelist" ? "#e3f2fd" : "#fff3e0",
              color:
                value === "Whitelist" ? "#1565c0" : "#ef6c00",
            }}
          />
        );
      },
    },
    {
      headerName: "Status",
      field: "status",
      flex: 1,
      cellRenderer: ({ value }) => {
        let styles = {};

        if (value === "Whitelisted") {
          styles = {
            backgroundColor: "#e8f5e9",
            color: "#2e7d32",
          };
        }

        if (value === "PENDING" || value === "REQUEST_INITIATED") {
          styles = {
            backgroundColor: "#fff8e1",
            color: "#f57c00",
          };
        }
        if (value === "ACTIVE" || value === "ONBOARDED") {
          styles = {
            backgroundColor: "#cce5ce",
            color: "#05962e",
          };
        }
        if (value === "INACTIVE" || value ==="REQUEST_FAILED") {
          styles = {
            backgroundColor: "#f0bcb6",
            color: "#f40c0c",
          };
        }
        if (value === "UPLOADED") {
          styles = {
            backgroundColor: "#e3f2fd",
            color: "#1565c0",
          };
        }

        if (value === "Blocked") {
          styles = {
            backgroundColor: "#ffebee",
            color: "#c62828",
          };
        }

        return (
          <Chip
            label={value}
            size="small"
            sx={{
              fontWeight: 600,
              ...styles,
              borderRadius: "8px",
            }}
          />
        );
      },
    },
  ];
  const hasActiveFilters =
    searchText.trim() !== "" ||
    selectedDomains.length > 0 ||
    (gridApi?.getSelectedRows()?.length ?? 0) > 0 ||
    Object.keys(gridApi?.getFilterModel() || {}).length > 0;

  // const handleClearFilters = () => {
  //   setSearchText("");
  //   setSelectedDomains([]);
  //   if (gridApi) {
  //     gridApi.setFilterModel(null);
  //     gridApi.setGridOption("quickFilterText", "");
  //     gridApi.deselectAll();
  //   }
  // };

  const handleClearFilters = () => {
    setRowData(allRowData);
    setDashboardFilter("");
    setDisableFilterBtn(true);

    if (gridRef.current) {
      gridRef.current.setFilterModel(null);
      gridRef.current.deselectAll();
    }
  };

  const handleExport = () => {
    if (!gridApi) return;

    const selectedRows = gridApi.getSelectedRows();

    gridApi.exportDataAsCsv({
      fileName: `domain-whitelist-${new Date().toISOString().slice(0, 10)}.csv`,
      columnKeys: [
        "alIdentifier",
        "requestNo",
        "domain",
        "requestedBy",
        "email",
        "requestDate",
        "status",
      ],
      onlySelected: selectedRows.length > 0,
    });
  };

  const onUpload = async (file) => {
    const formData = new FormData();
    formData.append("file", file);

    try {
      const response = await dispatch(
        postUploadDomainSlice({
          dynamicEndpoint: postUploadDomainEndpoint,
          data: formData, // or data: formData based on createApi
        }),
      );

      if (response.meta.requestStatus === "fulfilled") {
        const result = response.payload;

        if (result.failedCount > 0) {
          toast.warn(
            `Uploaded: ${result.successCount}, Failed: ${result.failedCount},
             Failed Domains:${result.failedDomains.join(", ")},
             Could not add domain. Either is duplicate or invalid.`,
          );
        } else {
          toast.success(`Upload successful: ${result.successCount}`);
        }

        getDomainList();
        setUploadOpen(false);
      } else {
        const res = response?.payload;
        toast.error(
          res?.technicalErrorMessage
            ? res?.technicalErrorMessage
            : res?.errorMessage
              ? res?.errorMessage
              : "Upload failed",
        );
      }
    } catch (error) {
      toast.error("Something went wrong");
      console.error(error);
    }
  };

  const handleSubmitAddDomainModal = async (data) => {
    try {
      const response = await dispatch(
        postAddDomainSlice({
          dynamicEndpoint: postAddDomainEndpoint,
          data,
        })
      );

      if (response?.meta?.requestStatus === "fulfilled") {
        const result = response.payload;

        if (result?.success) {
          toast.success(result.message, {
            autoClose: 2000,
          });

          handleCloseAddDomainModal();
          getDomainList();
        } else {
          // Domain already exists / Invalid domain format
          toast.error(result.message, {
            autoClose: 2000,
          });
        }
      } else {
        toast.error(
          response?.payload?.message || "Something went wrong. Please try again.",
          {
            autoClose: 2000,
          }
        );
      }
    } catch (error) {
      console.error("API error:", error);

      toast.error("Something went wrong. Please try again.", {
        autoClose: 2000,
      });
    }
  };

  const onClickAddDomain = () => {
    setOpenAddDomainModal(true)
  }

  const handleCloseAddDomainModal = () => {
    setOpenAddDomainModal(false);
  }

  return (
    <>
      <TopSection
        labelList={[
          {
            title: "Home",
            path: "/dashboard",
          },
          {
            title: "Admin",
          },
        ]}
        tabTitle="Manage Domain Whitelisting"
      />

      <>
        <Box>
          <Typography color="text.secondary">
            Manage and monitor all domain whitelisting requests.
          </Typography>
        </Box>
        <div
          style={{
            display: "flex",
            justifyContent: "end",
            marginRight: 20,
            marginTop: -50,
          }}
        >
          <CardContent>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
            >
              <Stack direction="row" spacing={1}>
                <ButtonComponent
                  type="secondary"
                  maxWidth="200px"
                  label="+ Add Domain"
                  height="40px"
                  onClick={onClickAddDomain}
                />

                <Button
                  variant="outlined"
                  startIcon={<UploadFileIcon />}
                  onClick={() => setUploadOpen(true)}
                  sx={{
                    color: "#1976d2",
                    borderColor: "#1976d2",
                    "&:hover": {
                      color: "#1976d2",
                      borderColor: "#1976d2",
                      backgroundColor: "transparent",
                      boxShadow: "none",
                    },
                  }}
                >
                  Upload Domain
                </Button>

                <Button
                  variant="contained"
                  disabled={!canWhitelist}
                  startIcon={<VerifiedIcon />}
                  onClick={() => setOpenWhitelistModal(true)}
                  sx={{
                    backgroundColor: "#2e7d32",
                    color: "#fff",
                    "&:hover": {
                      backgroundColor: "#2e7d32",
                      color: "#fff",
                      boxShadow: "none",
                    },
                  }}
                >
                  Whitelist Domain
                </Button>

                <Button
                  variant="contained"
                  disabled={!canBlacklist}
                  color="error"
                  startIcon={<BlockIcon />}
                  onClick={() => setOpenBlacklistModal(true)}
                  sx={{
                    backgroundColor: "#d32f2f",
                    color: "#fff",
                    "&:hover": {
                      backgroundColor: "#d32f2f",
                      color: "#fff",
                      boxShadow: "none",
                    },
                  }}
                >
                  Remove Whitelist
                </Button>
              </Stack>
            </Stack>
          </CardContent>
        </div>
        <Grid container spacing={1.5} mb={2.5}>
          {cards.map((item) => {
            const isClickable = [
              "DOMAINS WHITELISTED",
              "PARENT VENDORS ONBOARDED",
              // "PENDING WHITELIST",
            ].includes(item.title);

            return (
              <Grid item xs={12} md={2.4} key={item.title}>
                <Card
                  onClick={() => {
                    if (!isClickable) return;

                    if (item.title === "DOMAINS WHITELISTED") {
                      handleDashboardFilter("WHITELISTED");
                    } else if (item.title === "PARENT VENDORS ONBOARDED") {
                      handleDashboardFilter("ONBOARDED");
                    } 
                    // else if (item.title === "PENDING WHITELIST") {
                    //   handleDashboardFilter("PENDING");
                    // }
                  }}
                  sx={{
                    cursor: isClickable ? "pointer" : "default",
                    borderRadius: 2,
                    height: 122,
                    border: "1px solid #EAECF0",
                    boxShadow: "0 2px 10px rgba(16, 24, 40, 0.06)",
                    "&:hover": {
                      boxShadow: isClickable
                        ? "0 4px 16px rgba(16, 24, 40, 0.12)"
                        : "0 2px 10px rgba(16, 24, 40, 0.06)",
                    },
                  }}
                >
                  {/* Keep your existing CardContent exactly as it is */}
                  <CardContent sx={{ p: 1.5, "&:last-child": { pb: 1.5 } }}>
                    <Typography
                      fontSize={11}
                      color="#667085"
                      letterSpacing={0.4}
                      fontWeight={600}
                    >
                      {item.title}
                    </Typography>

                    {summaryLoading ? (
                      <>
                        <Skeleton
                          variant="text"
                          width="60%"
                          height={42}
                          sx={{ mt: 0.25 }}
                        />
                        <LinearProgress
                          sx={{ mt: 1, height: 6, borderRadius: 999 }}
                        />
                        <Skeleton
                          variant="text"
                          width="35%"
                          height={22}
                          sx={{ mt: 0.5 }}
                        />
                      </>
                    ) : (
                      <>
                        <Typography
                          mt={0.5}
                          fontSize={26}
                          fontWeight={700}
                          color={item.color}
                          lineHeight={1.1}
                        >
                          {item.value}
                        </Typography>

                        <LinearProgress
                          variant="determinate"
                          value={item.percent}
                          sx={{
                            mt: 1,
                            height: 6,
                            borderRadius: 999,
                            backgroundColor: "#EEF2F6",
                          }}
                        />

                        <Typography
                          mt={0.75}
                          fontWeight={600}
                          fontSize={12}
                          color="#475467"
                        >
                          {item.percent}%
                        </Typography>
                      </>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
          <TextField
            size="small"
            placeholder="Search..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{ width: 260 }}
          />
          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              disabled={disableFilterBtn}
              startIcon={<FilterAltOffIcon />}
              onClick={handleClearFilters}
              sx={{
                color: "#1976d2",
                borderColor: "#1976d2",
                "&:hover": { borderColor: "#1976d2", backgroundColor: "transparent" },
              }}
            >
              Clear Filters
            </Button>
            <Button
              variant="outlined"
              startIcon={<FileDownloadIcon />}
              onClick={handleExport}
              sx={{
                color: "#1976d2",
                borderColor: "#1976d2",
                "&:hover": { borderColor: "#1976d2", backgroundColor: "transparent" },
              }}
            >
              DOWNLOAD
            </Button>
          </Stack>
        </Box>
        <Box sx={{ position: "relative" }}>
          <div
            className="ag-theme-quartz"
            style={{ height: 400, width: "100%" }}
          >
            <AgGridReact
              ref={gridRef}
              rowData={rowData}
              columnDefs={columns}
              defaultColDef={defaultColDef}
              gridOptions={gridOptions}
              onGridReady={onGridReady}
              pagination={pagination}
              paginationPageSize={paginationPageSize}
              paginationPageSizeSelector={paginationPageSizeSelector}
              rowSelection={rowSelection}
              onSelectionChanged={(params) => {
                setSelectedDomains(params.api.getSelectedRows());
              }}
            />
          </div>
          {tableLoading && (
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                backgroundColor: "rgba(255,255,255,0.7)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Loader />
            </Box>
          )}
        </Box>
        <UploadDomainModal
          open={uploadOpen}
          handleClose={() => setUploadOpen(false)}
          onUpload={onUpload}
        />
        <WhitelistDomainModal
          open={openWhitelistModal}
          handleClose={() => setOpenWhitelistModal(false)}
          domains={selectedDomains}
          onSubmit={() => {
            getDomainList();
            getDashboardSummary();
            setSelectedDomains([]);
            if (gridRef.current) {
              gridRef.current.deselectAll();
            }
          }}
        />
        <BlacklistDomainModal
          open={openBlacklistModal}
          handleClose={() => setOpenBlacklistModal(false)}
          domains={selectedDomains}
          requestedBy={loggedInUser}
          onSubmit={() => {
            getDomainList();
            getDashboardSummary();
            setSelectedDomains([]);
            if (gridRef.current) {
              gridRef.current.deselectAll();
            }
          }}
        />
        <AddDomainModal
          openAddDomainModal={openAddDomainModal}
          handleCloseAddDomainModal={handleCloseAddDomainModal}
          handleSubmitAddDomainModal={handleSubmitAddDomainModal}
          rowData={rowData}
        />
      </>
    </>
  );
};

export default AdminManageWhitelistDomain;
